import type { ListBuilderConfiguration } from '../types';

/**
 * Task Opportunity Embedded list configuration
 * Shows tasks embedded in an opportunity view
 * Features completion icons, priority badges, and action icons
 * Converted from JSON to TypeScript for type safety
 */
export const TASK_OPPORTUNITY_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-tasks-opportunity-embedded',
			cacheTTL: 300000,
			name: 'Opportunity Tasks',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'completion-column',
					fields: [
						{
							id: 'completion-icon',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'status',
									objectTable: 'task',
								},
							},
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'complete',
							},
							styleConfig: {
								iconName: 'check',
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								valueMapping: [
									{
										color: { themeColor: 'primary' },
										value: 'completed',
									},
									{
										color: { themeColor: 'textMuted' },
										value: 'pending',
									},
								],
								tooltip: 'Complete',
							},
							displayStyle: 'icon',
						},
					],
					headers: [{ id: 'h-completion', text: '' }],
					width: 50,
					align: 'center',
					sortable: false,
				},
				{
					id: 'due-date-column',
					fields: [
						{
							id: 'due-date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'dueDate',
									objectTable: 'task',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								formatOptions: {
									dateFormat: 'weekday-short-with-time',
									timezoneField: 'timezone',
									timeField: 'dueTime',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-due-date', text: 'Due Date' }],
					width: 130,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'dueDate',
							objectTable: 'task',
						},
					],
				},
				{
					id: 'assigned-user-column',
					fields: [
						{
							id: 'assigned-user-name',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'assignedUserName',
									objectTable: 'task',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-assigned', text: 'Assigned To' }],
					width: 120,
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'assignedUserName',
							objectTable: 'task',
						},
					],
				},
				{
					id: 'subject-column',
					fields: [
						{
							id: 'task-subject',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'title',
									objectTable: 'task',
								},
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-subject',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'line_break',
						},
						{
							id: 'task-description',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'task',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
							truncate: true,
							maxLength: 100,
						},
					],
					headers: [
						{ id: 'h-subject', text: 'Subject' },
						{ id: 'h-description', text: 'Description' },
					],
					width: 250,
					sortable: false,
				},
				{
					id: 'priority-column',
					fields: [
						{
							id: 'priority-chip',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'priority',
									objectTable: 'task',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								valueMapping: [
									{
										color: { type: 'danger' },
										value: 'high',
										displayText: 'High',
									},
									{
										color: { type: 'warning' },
										value: 'medium',
										displayText: 'Medium',
									},
									{
										color: { type: 'neutral' },
										value: 'low',
										displayText: 'Low',
									},
								],
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h-priority', text: 'Priority' }],
					width: 100,
					align: 'center',
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'priority',
							objectTable: 'task',
						},
					],
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-due-date-column-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'taskKeywords',
			'taskStatus',
			'taskPriority',
			'taskDueDateRange',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/tasks/:id',
			objectLabel: 'Task',
			objectTable: 'task',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'title', fieldLabel: 'Title' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'priority', fieldLabel: 'Priority' },
				{ fieldName: 'dueDate', fieldLabel: 'Due Date' },
				{ fieldName: 'assignedUserName', fieldLabel: 'Assigned To' },
			],
		},
	],
	tableSorts: [],
};
